import React, { useState, useEffect, useCallback } from 'react';
import { isEmpty, debounce } from 'lodash';
import styled from 'styled-components';
import PageContainer from 'library/components/page-container';
import * as StyledMyJourney from 'apps/journey/pages/my-journey2/styled-my-journey';
import withUserOutcomes from 'apps/journey/hoc/with-user-outcomes';
import ProfileMasthead from 'apps/journey/components/profile-masthead';
import { array, func, object, bool } from 'prop-types';
import OutcomesModal from 'apps/journey/components/modals/outcomes/outcomes-modal';
import Connector from 'apps/journey/pages/my-journey2/components/connector';
import ActiveGoal from 'apps/journey/pages/my-journey2/context/active-goal';
import ReloadRecommendationsContext from 'apps/journey/pages/my-journey2/context/reload-recommendations-context';
import OutcomeCardFlip from 'apps/journey/pages/my-journey2/outcomes/outcome-card-flip';
import { withRouter } from 'library/util/core-router';
import { resolve } from 'library/helpers/path-resolver';
import AddOutcomeModal from 'apps/journey/pages/goals/components/add-outcome-modal';
import StepQuestionsProvider from 'apps/journey/pages/my-journey2/context/step-questions-provider';
import OutcomeLoading from 'apps/journey/pages/my-journey2/outcomes/outcome-loading';
import hasHoc from 'util/hoc/has-hoc';
import { UrlParams } from 'library/util/url-params';
import UserProfileSetup from 'apps/journey/components/user-profile-setup/user-profile-setup';
import useWithA11y from 'library/hooks/use-with-a11y';
import A11yContext from 'context/a11y-context';
import RecommendedOutcome from './outcomes/recommended-outcome';
import SortDropdown from '../career-exploration/components/filters/sort-dropdown';
import { StyledSortContainer as SSContainer } from 'apps/journey/pages/career-exploration/components/filters/styled-sort-dropdown';
import PollingData from 'library/components/polling-data';
import {
  SearchIcon,
  StyledSearchInnerContainer,
} from '../career-exploration/components/filters/styled-filter-controls';
import Input from 'pairin-library/components/form-components/input';

const url = new UrlParams('/journey/my-journey2');

const StyledSearchContainer = styled.div`
  min-width: 200px;
  margin-right: 1em;
`;

const StyledSortWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  margin-top: 1.5em;
`;

const StyledSortContainer = styled.div`
  display: flex;
  margin-right: 1.5em;
  .sort-dropdown-content {
    z-index: 9;
  }

  div {
    &${SSContainer} {
      min-width: 160px;
    }
  }
`;

const sortOptions = [
  { sortBy: 'created_at', name: 'Created At' },
  { sortBy: 'due_date', name: 'Due Date' },
  { sortBy: 'added_by', name: 'Added By' },
  { by_user_outcome_status: 'not_started', name: 'Not Started' },
  { by_user_outcome_status: 'in_progress', name: 'In Progress' },
  { by_user_outcome_status: 'completed', name: 'Completed' },
  { by_user_outcome_status: 'dismissed', name: 'Dismissed' },
];

const orderOptions = [
  { order: 'asc', name: 'Ascending' },
  { order: 'desc', name: 'Descending' },
];

const JourneyContent = ({ children, activeGoalId }) => {
  const { isTabbing } = useWithA11y();

  return (
    <PageContainer direction="row">
      <A11yContext.Provider value={{ isTabbing: isTabbing }}>
        <StyledMyJourney.OutcomesWrapper hasGoal={!!activeGoalId}>
          {children}
        </StyledMyJourney.OutcomesWrapper>
      </A11yContext.Provider>
    </PageContainer>
  );
};

const MyJourney = ({
  outcomes,
  recommendedOutcomes,
  reloadOutcomes,
  location,
  outcomes_loading,
  user,
}) => {
  const [sortValue, setSortValue] = useState(sortOptions[0]);
  const [order, setOrder] = useState(orderOptions[0]);
  const [reloadRecommendations, setReloadRecommendations] = useState(false);
  const [outcomeNameInput, setOutcomeNameInput] = useState('');

  const handleSort = (sortOption) => {
    if (!isEmpty(sortOption)) {
      setSortValue(sortOption[0]);
    }
  };

  const handleOrder = (orderOption) => {
    if (!isEmpty(orderOption)) {
      setOrder(orderOption[0]);
    }
  };

  const handleSearch = useCallback(
    debounce((e) => {
      setOutcomeNameInput(e.target?.value);
    }, 1000),
    []
  );

  useEffect(() => {
    reloadOutcomes(true, false, {
      sort_by: sortValue?.sortBy,
      order: order?.order,
      by_outcome_name: !isEmpty(outcomeNameInput)
        ? outcomeNameInput
        : undefined,
      by_user_outcome_status: sortValue?.by_user_outcome_status,
    });
  }, [outcomeNameInput, sortValue, order, reloadOutcomes]);

  const shouldShouldOutcomes = () => {
    const queryParams = new URLSearchParams(resolve(location, 'search', ''));
    return queryParams.get('addOutcomes') === 'true';
  };

  const [showOutcomesModal, toggleOutcomesModal] = useState(
    shouldShouldOutcomes()
  );
  const [activeOutcomeId, setActiveOutcomeId] = useState();
  const [activeGoalId, setActiveGoalId] = useState();

  useEffect(() => {
    if (url.has('outcome')) setActiveOutcomeId(Number(url.get('outcome')));
    if (url.has('goal')) setActiveGoalId(Number(url.get('goal')));
  }, []);

  useEffect(() => {
    activeOutcomeId
      ? url.set('outcome', activeOutcomeId)
      : url.delete(['outcome']);

    url.update();
  }, [activeOutcomeId]);

  useEffect(() => {
    activeGoalId ? url.set('goal', activeGoalId) : url.delete(['goal']);

    url.update();
  }, [activeGoalId]);

  const mapOutcomes = () => {
    return outcomes.map((outcome, i) => {
      return outcome.accepted_at ? (
        <OutcomeCardFlip
          key={i}
          indexNumber={i}
          activeOutcomeId={activeOutcomeId}
          outcome={outcome}
          isActive={activeOutcomeId === outcome.id}
          setActiveOutcomeId={setActiveOutcomeId}
          setActiveGoalId={setActiveGoalId}
          activeGoalId={activeGoalId}
          outcomes={outcomes}
        />
      ) : (
        <RecommendedOutcome outcome={outcome} key={i} />
      );
    });
  };

  const renderJourneyContent = () => (
    <StepQuestionsProvider uuid={user?.uuid}>
      <JourneyContent
        outcome={outcomes.find(({ id }) => id === activeOutcomeId)}
        activeGoalId={activeGoalId}
      >
        <StyledMyJourney.JourneyMapContainer>
          <OutcomesModal
            active={showOutcomesModal}
            handleSubmit={() => null}
            toggleActive={() => toggleOutcomesModal(!showOutcomesModal)}
            reloadOutcomes={reloadOutcomes}
            defaultOutcomeType={
              recommendedOutcomes?.length > 0 && 'recommendations'
            }
          />
          <StyledMyJourney.PageHeader>My Journey</StyledMyJourney.PageHeader>
          <Connector
            activeGoalId={activeGoalId}
            activeOutcomeId={activeOutcomeId}
            outcomes={outcomes}
          >
            {outcomes && mapOutcomes(outcomes)}
          </Connector>
          <StyledMyJourney.AddOutcomeButtonPosition>
            <AddOutcomeModal
              numRecommendedOutcomes={recommendedOutcomes?.length || 0}
              defaultOutcomeType={
                recommendedOutcomes?.length > 0 && 'recommendations'
              }
            />
          </StyledMyJourney.AddOutcomeButtonPosition>
        </StyledMyJourney.JourneyMapContainer>
      </JourneyContent>
    </StepQuestionsProvider>
  );

  const reloadRecommendationsWithTimeout = () => {
    setReloadRecommendations(true);
    setTimeout(
      () => setReloadRecommendations(false),
      __STOP_RECOMMENDATIONS_REFRESH_INTERVAL__ || 180000 // 3 minutes
    );
  };

  return (
    <ReloadRecommendationsContext.Provider
      value={{
        setReloadRecommendations: reloadRecommendationsWithTimeout,
      }}
    >
      {reloadRecommendations && (
        <PollingData
          query={() =>
            reloadOutcomes(true, false, {
              sort_by: sortValue?.sortBy,
              order: order?.order,
              by_outcome_name: !isEmpty(outcomeNameInput)
                ? outcomeNameInput
                : undefined,
              by_user_outcome_status: sortValue?.by_user_outcome_status,
            })
          }
          interval={__RECOMMENDATIONS_REFRESH_INTERVAL__ || 5000} // 5 seconds
        />
      )}
      <UserProfileSetup />
      <ProfileMasthead />
      <StyledSortWrapper>
        <StyledSortContainer>
          <StyledSearchContainer>
            <StyledSearchInnerContainer>
              <Input
                name="searchValue"
                label="Search by outcome name"
                type="text"
                value={outcomeNameInput}
                onChange={handleSearch}
              />
              <SearchIcon />
            </StyledSearchInnerContainer>
          </StyledSearchContainer>
          <SortDropdown
            sortOptions={sortOptions}
            sortValues={[sortValue]}
            handleChange={handleSort}
          />
          <SortDropdown
            sortOptions={orderOptions}
            sortValues={[order]}
            handleChange={handleOrder}
            label="Order"
          />
        </StyledSortContainer>
      </StyledSortWrapper>

      {showOutcomesModal && (
        <OutcomesModal
          active={showOutcomesModal}
          handleSubmit={() => null}
          toggleActive={() => toggleOutcomesModal(!showOutcomesModal)}
          reloadOutcomes={reloadOutcomes}
          defaultOutcomeType={
            recommendedOutcomes?.length > 0 && 'recommendations'
          }
        />
      )}
      {outcomes_loading ? (
        <OutcomeLoading />
      ) : (
        <ActiveGoal.Provider
          value={{
            url: url,
            activeGoalId: activeGoalId,
            setActiveGoalId: setActiveGoalId,
            reloadOutcomes: reloadOutcomes,
          }}
        >
          {renderJourneyContent()}
        </ActiveGoal.Provider>
      )}
    </ReloadRecommendationsContext.Provider>
  );
};

MyJourney.propTypes = {
  outcomes: array,
  reloadOutcomes: func,
  location: object,
  recommendedOutcomes: array,
  outcomes_loading: bool,
  user: object,
};

MyJourney.hoc_list = [withRouter, withUserOutcomes, 'withUser'];
export default hasHoc(MyJourney);
